/**
 * @fileoverview Zod schemas and types for the Invoices feature.
 * @module features/invoices/schemas/invoicesSchema
 */

import { z } from 'zod'

export const settlementTypeSchema = z.enum(['MANUAL_PAYMENT', 'PLATFORM_DEDUCTED'])
export type SettlementType = z.infer<typeof settlementTypeSchema>

/**
 * Single invoice response from backend.
 */
export const invoiceSchema = z.object({
  uuid: z.string().uuid(),
  invoiceNumber: z.string(),
  saleDate: z.string(),
  netAmount: z.number(),
  grossAmount: z.number(),
  vatRate: z.number().nullable().optional(),
  currency: z.string(),
  counterparty: z.string(),
  issueDate: z.string().nullable().optional(),
  paymentDueDate: z.string().nullable().optional(),
  originalFilename: z.string().nullable().optional(),
  hasFile: z.boolean(),
  settlementType: settlementTypeSchema.nullable().optional(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export type Invoice = z.infer<typeof invoiceSchema>

/**
 * Paginated invoice response. Accepts both legacy flat shape and Spring Boot 4's
 * nested `page` shape and normalizes pagination fields to the top level.
 */
const invoicePageMetadataSchema = z
  .object({
    size: z.number(),
    number: z.number(),
    totalElements: z.number(),
    totalPages: z.number(),
  })
  .passthrough()

export const invoicePageSchema = z
  .object({
    content: z.array(invoiceSchema).default([]),
    totalElements: z.number().optional(),
    totalPages: z.number().optional(),
    size: z.number().optional(),
    number: z.number().optional(),
    page: invoicePageMetadataSchema.optional(),
  })
  .transform(raw => ({
    content: raw.content,
    size: raw.page?.size ?? raw.size,
    number: raw.page?.number ?? raw.number,
    totalElements: raw.page?.totalElements ?? raw.totalElements,
    totalPages: raw.page?.totalPages ?? raw.totalPages,
  }))

export type InvoicePage = z.infer<typeof invoicePageSchema>

/**
 * Request body for creating an invoice.
 */
export const createInvoiceRequestSchema = z.object({
  invoiceNumber: z.string().min(1).max(100),
  saleDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
  netAmount: z.number().min(0),
  grossAmount: z.number().min(0),
  vatRate: z.number().min(0).nullable().optional(),
  currency: z.string().regex(/^[A-Z]{3}$/),
  counterparty: z.string().min(1).max(255),
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  paymentDueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
})

export type CreateInvoiceRequest = z.infer<typeof createInvoiceRequestSchema>

/**
 * Request body for updating an invoice (partial — all fields optional).
 */
export const updateInvoiceRequestSchema = z.object({
  invoiceNumber: z.string().min(1).max(100).optional(),
  saleDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).optional(),
  netAmount: z.number().min(0).optional(),
  grossAmount: z.number().min(0).optional(),
  vatRate: z.number().min(0).nullable().optional(),
  currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  counterparty: z.string().min(1).max(255).optional(),
  issueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  paymentDueDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().optional(),
  settlementType: settlementTypeSchema.nullable().optional(),
  clearSettlementType: z.boolean().optional(),
})

export type UpdateInvoiceRequest = z.infer<typeof updateInvoiceRequestSchema>

/**
 * Response from the parse endpoint — all fields nullable (extraction may be partial).
 */
export const parsedInvoiceResponseSchema = z.object({
  invoiceNumber: z.string().nullable(),
  saleDate: z.string().nullable(),
  netAmount: z.number().nullable(),
  grossAmount: z.number().nullable(),
  vatRate: z.number().nullable(),
  currency: z.string().nullable(),
  counterparty: z.string().nullable(),
  issueDate: z.string().nullable(),
  paymentDueDate: z.string().nullable(),
})

export type ParsedInvoiceResponse = z.infer<typeof parsedInvoiceResponseSchema>

/**
 * Request payload for linking payments to an invoice.
 */
export const linkInvoicePaymentsRequestSchema = z.object({
  paymentUuids: z.array(z.string().uuid()).min(1).max(50),
})

export type LinkInvoicePaymentsRequest = z.infer<typeof linkInvoicePaymentsRequestSchema>

export const invoiceLinkPaymentSchema = z
  .object({
    paymentUuid: z.string().uuid(),
    txnDate: z.string().nullable().optional(),
    title: z.string().nullable().optional(),
    counterpartyName: z.string().nullable().optional(),
    reportingCurrency: z.string().nullable().optional(),
    reportingAmount: z.number().nullable().optional(),
  })
  .passthrough()

export type InvoiceLinkPayment = z.infer<typeof invoiceLinkPaymentSchema>

/**
 * Minimal schema for invoice-links response used by drag-and-drop linking.
 */
export const invoiceLinksResponseSchema = z
  .object({
    invoiceUuid: z.string().uuid(),
    invoiceNumber: z.string().optional(),
    counterparty: z.string().optional(),
    payments: z
      .array(invoiceLinkPaymentSchema)
      .default([]),
  })
  .passthrough()

export type InvoiceLinksResponse = z.infer<typeof invoiceLinksResponseSchema>
